import React from 'react';
import { Navigate } from 'react-router-dom';
import { useNavDiet } from '@/config/navDiet';
import { isMaieShell } from '@/config/maieShell';

/**
 * A route that moved under NAV_DIET_ENABLED.
 *
 * The nav diet relocated nine destinations — /admin/* into /clinic/*,
 * /lab-orders and friends into /orders, /dashboard onto the home page. Making
 * those redirects unconditional would have turned the flag into a one-way
 * door: a clinic put back on the old sidebar would still have its Team page
 * bounce to a Clinic settings frame that the old nav never mentions.
 *
 * So the redirect is part of the flag. On, the URL forwards to its new home;
 * off, it renders exactly what it always rendered. Either way the URL resolves
 * — nobody's bookmark or notification link lands on a 404.
 *
 * THE DESIGN SHELL IS A SECOND SUPERSESSION, and a later one. A page the diet
 * merely MOVED, the redesign may have REPLACED — `/dashboard` is the doctor's
 * Today now, not a clinic-scoped home. Passing `maieTo` says where this URL
 * goes under the design; it wins over `to`, because it is the newer answer.
 * Without it, a superseded page renders inside the new chrome and draws its own
 * heading under the shell's — the doubled title that gives these pages away.
 */
export const NavDietRoute: React.FC<{
  /** Where this destination lives once the diet is on. */
  to: string;
  /** Where it lives under the DESIGN shell, when that differs. Wins over `to`. */
  maieTo?: string;
  /** What this URL rendered before the diet. */
  children: React.ReactNode;
}> = ({ to, maieTo, children }) => {
  const { navDiet, resolved } = useNavDiet();
  // Checked before the diet gate: the design shell's answer does not depend on
  // a per-tenant flag that may still be resolving.
  if (maieTo && isMaieShell()) return <Navigate to={maieTo} replace />;
  // Hold on a tenant's first ever load: rendering the old page and bouncing a
  // frame later would push a junk entry into history and flash a page the
  // user never asked for.
  if (!resolved) return null;
  return navDiet ? <Navigate to={to} replace /> : <>{children}</>;
};

export default NavDietRoute;
